import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import {
  getContrastThemeClass,
  resolveColorProperty,
  useFallbackTranslation,
} from '@gn-audio/jss-components';

import {
  JabraRadioButton,
  JabraRadioButtonTheme,
  JabraRadioGroup,
} from 'common/components/JabraRadioGroup';
import { SoundDemoClipSwitch } from 'components/Product/SoundDemo/components/SoundDemoClipSwitch';
import { SoundDemoClipToggleGroup } from 'components/Product/SoundDemo/components/SoundDemoClipToggleGroup';
import { SoundDemoEnvironmentSelect } from 'components/Product/SoundDemo/components/SoundDemoEnvironmentSelect';
import { SoundDemoPlayer } from 'components/Product/SoundDemo/components/SoundDemoPlayer';
import { SoundDemoClip, SoundDemoFields } from 'components/Product/SoundDemo/soundDemoTypes';
import { useSpotTracking } from 'utils/hooks/useSpotTracking';

const SoundDemo: FC = ({ fields }: { fields: SoundDemoFields }) => {
  const { t } = useFallbackTranslation();
  const { BackgroundColor, DisclaimerText, Theme, SoundDemoEnvironments, trackingSpotName } =
    fields;
  const spotRef = useSpotTracking(trackingSpotName?.value);
  const defaultSoundDemoEnvironment = useMemo(
    () => SoundDemoEnvironments?.[0]?.EnvironmentName?.value || '',
    [SoundDemoEnvironments]
  );
  const defaultSoundDemoClip = useMemo(
    () => SoundDemoEnvironments?.[0]?.SoundDemoClips?.[0]?.SoundDemoName?.value || '',
    [SoundDemoEnvironments]
  );

  const [selectedDemoEnvironment, setSelectedDemoEnvironment] = useState<string>(
    defaultSoundDemoEnvironment
  );

  const [selectedDemoClip, setSelectedDemoClip] = useState<string>(defaultSoundDemoClip);

  useEffect(() => {
    setSelectedDemoClip(defaultSoundDemoClip);
  }, [defaultSoundDemoClip, selectedDemoEnvironment]);

  const [selectOpen, setSelectOpen] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  const currentClips = useMemo(
    () =>
      SoundDemoEnvironments.find(
        (environment) => environment.EnvironmentName?.value === selectedDemoEnvironment
      )?.SoundDemoClips ?? [],
    [SoundDemoEnvironments, selectedDemoEnvironment]
  );

  useEffect(() => {
    Object.entries(audioRefs.current).forEach(([id, audio]) => {
      if (!audio) return;
      if (isPlaying && currentClips.some((clip) => clip.id === id)) {
        audio.currentTime = 0;
        audio.play().catch(() => undefined);
      } else {
        audio.pause();
      }
    });
  }, [isPlaying, currentClips]);

  const clipIsEnded = (clip: SoundDemoClip) => {
    const audio = audioRefs.current[clip.id];
    return !audio || audio.paused || audio.ended;
  };

  const backgroundColor = resolveColorProperty(BackgroundColor?.value || '');
  const isSingleDemoEnvironment = SoundDemoEnvironments.length === 1;

  const theme = Theme?.value || 'light';
  const demoEnvironmentPlayerClasses = isSingleDemoEnvironment
    ? ` ${theme === 'light' ? 'text-black' : 'text-primary'}`
    : 'group bg-primary text-on-brand has-[.env-select:hover]:bg-primary has-[.env-select:hover]:text-on-brand';
  const componentBackgroundColor =
    theme === 'light'
      ? 'bg-gray-50 text-black shadow-[inset_0px_2px_2px_0px_rgba(0,0,0,0.25)]'
      : 'bg-gray-900 text-white';

  const spotThemeClass = getContrastThemeClass(BackgroundColor?.value as string);

  const soundPlayer = () => (
    <SoundDemoPlayer isPlaying={isPlaying} onToggle={() => setIsPlaying((playing) => !playing)} />
  );

  if (SoundDemoEnvironments.length === 0) return null;

  return (
    <div
      ref={spotRef}
      data-wt-spot={trackingSpotName?.value}
      style={{
        backgroundColor: backgroundColor,
      }}
    >
      <div className="container flex flex-col items-center justify-center text-lg">
        <div
          className={classNames(
            'hidden h-16 items-center justify-center rounded-full lg:flex',
            componentBackgroundColor
          )}
        >
          <div className={`${selectOpen ? 'rounded-t-[2.5rem] bg-gray-900' : ''}`}>
            <div
              className={classNames(
                `flex h-16 w-60 rounded-full transition-colors duration-300 ease-linear hover:bg-gray-700 hover:text-white`,
                demoEnvironmentPlayerClasses
              )}
            >
              <div className={`env-play ${isSingleDemoEnvironment ? 'w-full' : 'w-3/4'}`}>
                {soundPlayer()}
              </div>
              {!isSingleDemoEnvironment && (
                <SoundDemoEnvironmentSelect
                  key={selectedDemoEnvironment}
                  SoundDemoEnvironments={SoundDemoEnvironments}
                  selectOpen={selectOpen}
                  setSelectOpen={setSelectOpen}
                  selectedDemoEnvironment={selectedDemoEnvironment}
                  setSelectedDemoEnvironment={setSelectedDemoEnvironment}
                  setIsPlaying={setIsPlaying}
                />
              )}
            </div>
          </div>
          <div className="px-16">
            {SoundDemoEnvironments.map(
              (environment) =>
                environment.EnvironmentName?.value === selectedDemoEnvironment &&
                (environment.SoundDemoClips.length > 2 ? (
                  <SoundDemoClipToggleGroup
                    SoundDemoEnvironment={environment}
                    setSelectedDemoClip={setSelectedDemoClip}
                  />
                ) : (
                  <div key={environment.id}>
                    <SoundDemoClipSwitch
                      SoundDemoEnvironment={environment}
                      setSelectedDemoClip={setSelectedDemoClip}
                      theme={theme}
                    />
                  </div>
                ))
            )}
          </div>
        </div>

        <div
          className={classNames(
            'block w-80 rounded-b-3xl rounded-t-[2.5rem] md:w-96 lg:hidden',
            componentBackgroundColor
          )}
        >
          <div className="flex items-center justify-center rounded-full bg-primary text-on-brand">
            <div className="w-60">{soundPlayer()}</div>
          </div>
          <div className="px-6 py-8">
            {SoundDemoEnvironments.length > 1 && (
              <>
                <span className="capitalize">{t('system.Environments')}</span>
                <div className="p-1 pb-2">
                  <JabraRadioGroup
                    title={t('system.Environments')}
                    onValueChange={(value) => {
                      setSelectedDemoEnvironment(value);
                      setIsPlaying(false);
                    }}
                    selected={selectedDemoEnvironment}
                    className="flex flex-col gap-1"
                  >
                    {SoundDemoEnvironments.map((environment) => (
                      <JabraRadioButton
                        key={environment.id}
                        value={environment.EnvironmentName?.value}
                        title={environment.EnvironmentName?.value}
                        theme={
                          theme === 'light'
                            ? JabraRadioButtonTheme.light
                            : JabraRadioButtonTheme.dark
                        }
                      />
                    ))}
                  </JabraRadioGroup>
                </div>
                <div className="pborder-gray-700 w-full border-t p-1"></div>
              </>
            )}
            <span className="capitalize">{t('system.TryTheFeatures')}</span>
            <div className="p-1">
              <JabraRadioGroup
                title={t('system.TryTheFeatures')}
                onValueChange={setSelectedDemoClip}
                selected={selectedDemoClip}
                className="flex flex-col gap-1"
              >
                {SoundDemoEnvironments.map(
                  (environment) =>
                    environment.EnvironmentName?.value === selectedDemoEnvironment &&
                    environment.SoundDemoClips.map((clip) => (
                      <JabraRadioButton
                        key={clip.id}
                        value={clip.SoundDemoName?.value}
                        title={clip.SoundDemoName?.value}
                        theme={
                          theme === 'light'
                            ? JabraRadioButtonTheme.light
                            : JabraRadioButtonTheme.dark
                        }
                      />
                    ))
                )}
              </JabraRadioGroup>
            </div>
          </div>
        </div>
        <div
          className={classNames('paragraph-small mt-6 text-center', spotThemeClass)}
          dangerouslySetInnerHTML={{ __html: sanitize(DisclaimerText?.value) }}
        />
        {SoundDemoEnvironments.map((environment) =>
          environment.SoundDemoClips.map(
            (clip) =>
              !!clip.SoundDemoFile.value && (
                <audio
                  key={clip.id}
                  ref={(el) => {
                    audioRefs.current[clip.id] = el;
                  }}
                  preload="auto"
                  muted={clip.SoundDemoName?.value !== selectedDemoClip}
                  src={clip.SoundDemoFile.value.src}
                  onEnded={() => {
                    if (currentClips.every(clipIsEnded)) {
                      setIsPlaying(false);
                    }
                  }}
                />
              )
          )
        )}
      </div>
    </div>
  );
};

export default withDatasourceCheck()(SoundDemo);
